import React from "react";
import { useFormContext, Control } from "react-hook-form";
import { Divider } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import CField, { CFieldInputProps } from "$forms/CField";
import CompanySelect from "$forms/CompanySelect";
import { CompanyType, Warehouse } from "$types";
import { useEntrepotForm } from "../context/EntrepotFormContext";
import useCompany from "$hooks/useCompany";
import { CompanyServices } from "$services";
import useAuth from "$hooks/useAuth";
import ManageableCompanies from "$containers/Entrepot/ManageableCompanies";
import IsManageableCheckbox from "$containers/Entrepot/isManageableCheckbox";

export const EntrepotFormCompanies: React.FC = () => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<Warehouse>();

  const { isAdmin } = useAuth();
  const { currentParentCompany, parentCompanyId, entrepot } = useEntrepotForm();
  const {
    isContributor,
    isLogistician,
    isClient: isClientCompany,
  } = useCompany();

  const { data: parentCompany } = useQuery({
    queryKey: ["getCompanyById", currentParentCompany],
    queryFn: () => CompanyServices.getCompanyById(currentParentCompany),
    enabled: !!currentParentCompany,
    staleTime: 60 * 1000,
  });

  /**
   * Can have related companies if:
   * - "companies" value is already present
   * - "parentCompany" is a contributor or a logistician
   */
  const canHaveCompanies = React.useMemo(() => {
    if (watch("companies")?.length > 0) {
      return true;
    }
    if (parentCompany?.length > 0) {
      return false;
    }
    return (
      isContributor(parentCompany?.companyTypes) ||
      isLogistician(parentCompany?.companyTypes)
    );
  }, [watch, isContributor, isLogistician, parentCompany]);

  return (
    <>
      {canHaveCompanies && (
        <CField
          control={control}
          name="companies"
          error={errors.companies}
          label={`Entreprise(s) cliente(s) rattachée(s) à l'entrepôt${
            isLogistician(parentCompany?.companyTypes) ? " logisiticien" : ""
          }`}
        >
          {(props: CFieldInputProps) => (
            <CompanySelect
              companyTypes={[CompanyType.CLIENT]}
              {...props}
              multiple
              value={props.value?.map((v) => v._id)}
              onChange={(companyIds: string[]) => {
                setValue(
                  "companies",
                  companyIds.map((cId) => ({
                    _id: cId,
                    isManageable: true,
                  }))
                );
              }}
              companyOptions={
                isContributor(parentCompany?.companyTypes)
                  ? parentCompany?.contributorOf
                  : undefined
              }
            />
          )}
        </CField>
      )}

      {isAdmin() && (
        <>
          <ManageableCompanies control={control as Control<Warehouse>} />
          {isClientCompany(parentCompany?.companyTypes) && (
            <>
              {watch("companies")?.length > 0 && <Divider />}
              <IsManageableCheckbox
                companyId={parentCompanyId || entrepot?.parentCompany}
                formControl={control as Control<Warehouse>}
                formFieldName="isManageable"
              />
            </>
          )}
        </>
      )}
    </>
  );
};
